import {
  AlertTriangle,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
} from 'lucide-react';
import * as React from 'react';
import {
  formatPlainDate,
  ROLE_LABELS,
  ROLES,
  today,
  type PlainDate,
  type Role,
} from '@cfl/shared';

import {
  Badge,
  Button,
  Card,
  CardContent,
  Combobox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { LostArt } from '@/components/illustrations';
import { Typewriter } from '@/components/typewriter';
import { BulkImportButton } from '@/components/bulk-import-dialog';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { ServerExportButton } from '@/components/server-export-button';
import { PlainDatePicker } from '@/components/plain-date-picker';
import { PageContainer } from '@/components/shell/page-container';
import { StatTile } from '@/components/stat-tile';
import { employeeStatusVariant, PILL_CLASS } from '@/lib/badges';
import {
  flattenDepartments,
  useChangeStatus,
  useDepartments,
  useEmployees,
  type Employee,
  type EmployeeFilters,
} from '@/lib/people-api';
import { useCurrentUser } from '@/lib/session';
import { EmployeeDialog } from '@/routes/hr/onboard-dialog';
import { PeopleImportDialog } from '@/routes/hr/people-import-dialog';
import { SkillsDialog } from '@/routes/hr/skills-dialog';

const ALL = '__all__';

export function EmployeesPage() {
  const me = useCurrentUser();
  const [search, setSearch] = React.useState('');
  const [departmentId, setDepartmentId] = React.useState(ALL);
  const [role, setRole] = React.useState(ALL);
  const [onlyNeedsSetup, setOnlyNeedsSetup] = React.useState(false);
  const [onboardOpen, setOnboardOpen] = React.useState(false);
  const [importOpen, setImportOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Employee | null>(null);
  const [skillsFor, setSkillsFor] = React.useState<Employee | null>(null);
  /** Held between picking "Mark as exited" and confirming it. */
  const [exiting, setExiting] = React.useState<Employee | null>(null);
  const [exitDate, setExitDate] = React.useState<PlainDate>(today());

  // Debounced so typing does not fire a request per keystroke.
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  React.useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(id);
  }, [search]);

  const filters: EmployeeFilters = {
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(departmentId !== ALL ? { departmentId } : {}),
    ...(role !== ALL ? { role: role as Role } : {}),
    ...(onlyNeedsSetup ? { needsOnboarding: true } : {}),
    pageSize: 100,
  };

  /**
   * The same filters, as a query string for the CSV route.
   *
   * `pageSize` is left off deliberately: the report route replaces it with its own
   * export ceiling, so sending the screen's 100 would cap the export at one page -
   * the exact failure the server route exists to avoid.
   */
  const peopleReportQuery = React.useMemo(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (departmentId !== ALL) params.set('departmentId', departmentId);
    if (role !== ALL) params.set('role', role);
    if (onlyNeedsSetup) params.set('needsOnboarding', 'true');
    // `toString()`, not `params.size` — that property is recent enough to be missing
    // on browsers this app otherwise supports.
    const query = params.toString();
    return query ? `?${query}` : '';
  }, [debouncedSearch, departmentId, role, onlyNeedsSetup]);

  const { data, isPending, error } = useEmployees(filters);
  const { data: departmentTree } = useDepartments();
  const departments = React.useMemo(
    () => flattenDepartments(departmentTree ?? []),
    [departmentTree],
  );

  const changeStatus = useChangeStatus();

  const rows = data?.items ?? [];
  const needsSetup = rows.filter((e) => e.needsOnboarding).length;
  const neverSignedIn = rows.filter((e) => !e.hasSignedIn).length;
  const withReports = rows.filter((e) => e.directReportCount > 0).length;

  async function onExit(employee: Employee, exitedOn: PlainDate) {
    try {
      await changeStatus.mutateAsync({ id: employee.id, status: 'EXITED', exitedOn });
      toast.success(`${employee.name} marked as exited on ${formatPlainDate(exitedOn)}`);
    } catch (err) {
      toast.error('Could not mark as exited', {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  }

  return (
    <PageContainer>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Employees"
          value={data?.total ?? '—'}
          caption={rows.length === data?.total ? 'all shown' : `${rows.length} shown`}
        />
        <StatTile
          label="Needs setup"
          value={needsSetup}
          tone={needsSetup > 0 ? 'warning' : 'default'}
          caption={
            needsSetup === 0
              ? 'everyone has a department and manager'
              : 'missing a department or manager'
          }
        />
        <StatTile
          label="Never signed in"
          value={neverSignedIn}
          caption={neverSignedIn === 0 ? 'everyone has logged in' : 'invited, not yet logged in'}
        />
        <StatTile
          label="Managers"
          value={withReports}
          caption="have at least one direct report"
        />
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 py-4 sm:py-4">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            {!search && (
              <span className="pointer-events-none absolute left-8 right-3 top-1/2 -translate-y-1/2 truncate text-sm text-muted-foreground">
                <Typewriter text="Search by name or email…" />
              </span>
            )}
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search employees by name or email"
              className="pl-8"
            />
          </div>

          <Combobox
            className="w-[200px]"
            aria-label="Filter by department"
            value={departmentId}
            // A filter, so select-only: creating a department nobody is in yet would
            // filter the table to nothing, which is a strange way to make one.
            onChange={setDepartmentId}
            options={[
              { value: ALL, label: 'All departments' },
              ...departments.map((d) => ({
                value: d.id,
                label: d.label,
                hint: `${d.employeeCount}`,
              })),
            ]}
            searchPlaceholder="Search departments"
            emptyText="No department matches"
          />

          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="w-[170px]">
              <SelectValue placeholder="All roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All roles</SelectItem>
              {ROLES.map((r) => (
                <SelectItem key={r} value={r}>
                  {ROLE_LABELS[r]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button hint={"Show only people whose setup is incomplete"}
            data-guide="needs-setup"
            variant={onlyNeedsSetup ? 'default' : 'outline'}
            onClick={() => setOnlyNeedsSetup((v) => !v)}
            className="gap-2"
          >
            <AlertTriangle className="size-4" />
            Needs setup
          </Button>

          {/* Server-side, so it exports every page of the current filter rather than
              the hundred rows on screen. The filters here are already server-side, so
              the same query goes to the report route and the export matches the table
              exactly. */}
          <div className="ml-auto flex items-center gap-2">
            <ServerExportButton
              path={`/reports/people.csv${peopleReportQuery}`}
              filename="people"
            />
            <BulkImportButton onClick={() => setImportOpen(true)}>Bulk onboard</BulkImportButton>
            <Button hint={"Add a new employee"} className="gap-2" onClick={() => setOnboardOpen(true)}>
              <Plus className="size-4" strokeWidth={2.4} />
              Onboard employee
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 sm:p-0">
          {/* Own scroll container: without it a wide table pushes the page body
              sideways and every column header drifts off the content edge. */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[220px]">Employee</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Designation</TableHead>
                  <TableHead>Reports to</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {isPending &&
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={7}>
                        <Skeleton className="h-6 w-full" />
                      </TableCell>
                    </TableRow>
                  ))}

                {error && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-10 text-center text-sm text-destructive">
                      {error.message}
                    </TableCell>
                  </TableRow>
                )}

                {!isPending && !error && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <EmptyState
                        art={LostArt}
                        title="No employees match these filters"
                        body="Anyone signing in with an @innoctive.com account is added automatically as an Employee."
                      />
                    </TableCell>
                  </TableRow>
                )}

                {rows.map((employee) => (
                  <TableRow key={employee.id}>
                    <TableCell>
                      <div className="font-medium">{employee.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {/* The employee ID sits with the email rather than in its own
                            column: it is an identifier you read off a row you have
                            already found, not something you scan a column of. Muted
                            when absent - most people have one, and the few who do not
                            are HR's to fill in, not an error to flag in red. */}
                        {employee.employeeCode && (
                          <>
                            <span className="tabular-nums">{employee.employeeCode}</span>
                            <span className="mx-1.5 opacity-40">·</span>
                          </>
                        )}
                        {employee.email}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {employee.departmentName ?? (
                        <span className="text-warning">Not set</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {employee.designationTitle ?? '—'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {employee.reportingManagerName ?? (
                        // Without a manager the manager stage of their assessment
                        // has nobody able to action it.
                        <span className="text-warning">Not set</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="soft-muted" className={PILL_CLASS}>
                        {ROLE_LABELS[employee.role]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={employeeStatusVariant(employee.status)} className={PILL_CLASS}>
                        {employee.hasSignedIn ? employee.status.replace(/_/g, ' ') : 'Invited'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" aria-label={`Actions for ${employee.name}`} data-guide="employee-actions">
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuItem data-guide="employee-edit" onSelect={() => setEditing(employee)}>
                            <Pencil className="size-4" />
                            Edit details
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => setSkillsFor(employee)}>
                            Skills and gaps
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {/* A menu item one row below "Edit details", and it signs
                              somebody out of the product on the spot — so it proposes
                              rather than acts. */}
                          <DropdownMenuItem
                            disabled={employee.id === me.id || employee.status === 'EXITED'}
                            onSelect={() => {
                              setExitDate(today());
                              setExiting(employee);
                            }}
                            className="text-destructive"
                          >
                            Mark as exited
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <EmployeeDialog open={onboardOpen} onOpenChange={setOnboardOpen} />
      <PeopleImportDialog open={importOpen} onOpenChange={setImportOpen} />
      <EmployeeDialog
        employee={editing}
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
      />
      <SkillsDialog employee={skillsFor} onOpenChange={(open) => !open && setSkillsFor(null)} />

      <ConfirmDialog
        open={exiting !== null}
        onOpenChange={(next) => !next && setExiting(null)}
        title={`Mark ${exiting?.name ?? ''} as exited?`}
        description="They are signed out everywhere immediately and cannot sign back in. Their goals, assessments and ratings are all kept. Someone who still has direct reports cannot be exited until those reports are moved to another manager."
        confirmLabel="Mark as exited"
        destructive
        pending={changeStatus.isPending}
        onConfirm={() => {
          const employee = exiting;
          setExiting(null);
          if (employee) void onExit(employee, exitDate);
        }}
      >
        {/* Defaults to today; pick an earlier day when recording someone who already left. */}
        <div className="space-y-2">
          <Label htmlFor="exit-date">Exit date</Label>
          <PlainDatePicker
            id="exit-date"
            value={exitDate}
            onChange={setExitDate}
            max={today()}
            className="w-full"
          />
        </div>
      </ConfirmDialog>
    </PageContainer>
  );
}
